// Components
// Libs
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';

// API
import postShippingRequest from '@/common/api/orders/postShippingRequest';
import Snack from '@/common/components/molecules/Snack/Snack';
// Contexts
import { useModalContext } from '@/common/contexts/ModalContext';

const useShippingRequest = ({ disabled, isLoading, isSuccess }) => {
  const { t } = useTranslation();
  const { setModalIsOpen } = useModalContext();

  /**
   * Set shipping request
   * Get mutator and set the mutation api
   */
  const {
    mutate: mutatePostShippingRequest,
    isLoading: isLoadingPostShippingRequest,
    isSuccess: isSuccessPostShippingRequest,
  } = useMutation(postShippingRequest);

  /**
   * Set shipping request for an order
   * @param {string} orderId
   * @param {array} items : Array of items to ship
   * @param {boolean} hasNoSelection if items is empty
   * @param {string} comment
   * @param {object} actions : Set of formik methods (setError ...)
   * @param {function} onSuccess
   */
  const onShippingRequest = useCallback(
    ({
      orderId,
      items,
      hasNoSelection,
      comment,
      actions,
      onSuccess = () => {},
    }) => {
      mutatePostShippingRequest(
        {
          orderId,
          items,
          hasNoSelection,
          comment,
        },
        {
          onSuccess: () => {
            onSuccess();
            setModalIsOpen(false);

            toast.success(
              <Snack message={t('common:messages:forms.success.default')} />,
            );
          },
          onError: (error) => {
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              if (error.response.data.errors) {
                actions.setErrors(error.response.data.errors);
              }

              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [mutatePostShippingRequest, setModalIsOpen, t],
  );

  /**
   * Check if action is disabled
   * @type {boolean}
   */
  const isDisabled = useMemo(
    () => ({
      loading: isLoading || isLoadingPostShippingRequest,
      disabled: disabled || isLoading || isLoadingPostShippingRequest,
    }),
    [disabled, isLoading, isLoadingPostShippingRequest],
  );

  return {
    isDisabled,
    isSuccess: isSuccess || isSuccessPostShippingRequest,
    onShippingRequest,
  };
};

export default useShippingRequest;
